/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import {
  useCallback,
  useRef,
  useState,
  useEffect,
} from '@web-stories-wp/react';
import styled from 'styled-components';
import { useKeyDownEffect } from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import useMedia from '../../../../../app/media/useMedia';
import { useConfig } from '../../../../../app/config';
import { PROVIDERS } from '../../../../../app/media/media3p/providerConfiguration';
import ProviderTab from './providerTab';
import { PANE_PADDING } from '../../shared';

const Section = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 8px;
  padding: 0 ${PANE_PADDING};
`;

function ProviderTabList({ providers }) {
  const { isRTL } = useConfig();
  const ref = useRef();

  const { selectedProvider, setSelectedProvider } = useMedia(
    ({
      media3p: {
        state: { selectedProvider },
        actions: { setSelectedProvider },
      },
    }) => ({
      selectedProvider,
      setSelectedProvider,
    })
  );

  const [focusedIndex, setFocusedIndex] = useState(0);

  const providerCount = providers.length;

  const isProviderActive = useCallback(
    ({ code, mediaProviderId }) => {
      if (!selectedProvider) {
        return false;
      }
      const matchesCode =
        code === selectedProvider?.providerConfigurationKey;
      if (!matchesCode) {
        return false;
      }
      if (mediaProviderId && selectedProvider?.mediaProviderId) {
        return mediaProviderId === selectedProvider.mediaProviderId;
      }
      return !mediaProviderId && !selectedProvider?.mediaProviderId;
    },
    [selectedProvider]
  );

  useEffect(() => {
    const activeIndex = providers.findIndex((provider) =>
      isProviderActive(provider)
    );
    if (activeIndex >= 0) {
      setFocusedIndex(activeIndex);
    } else if (providers.length > 0) {
      setFocusedIndex(0);
    }
  }, [providers, isProviderActive]);

  const focusByIndex = useCallback((newIndex) => {
    if (!ref.current) {
      return;
    }
    const container = ref.current;
    const newFocusedTab = container.children[newIndex];
    newFocusedTab.focus();
  }, []);

  const selectFocused = useCallback(() => {
    const provider = providers[focusedIndex];
    if (!provider) {
      return;
    }
    setSelectedProvider(provider.provider);
  }, [focusedIndex, setSelectedProvider, providers]);

  const dir = isRTL ? -1 : 1;

  useKeyDownEffect(
    ref,
    'left',
    () => focusByIndex((focusedIndex - dir + providerCount) % providerCount),
    [focusByIndex, focusedIndex, dir, providerCount]
  );

  useKeyDownEffect(
    ref,
    'right',
    () => focusByIndex((focusedIndex + dir + providerCount) % providerCount),
    [focusByIndex, focusedIndex, dir, providerCount]
  );

  useKeyDownEffect(ref, 'home', () => focusByIndex(0), [focusByIndex]);

  useKeyDownEffect(ref, 'end', () => focusByIndex(providerCount - 1), [
    focusByIndex,
    providerCount,
  ]);

  useKeyDownEffect(ref, ['enter', 'space'], selectFocused, [selectFocused]);

  return (
    <Section ref={ref}>
      {providers.map((providerInfo, index) => (
        <ProviderTab
          key={providerInfo.key}
          index={index}
          name={
            providerInfo.displayName ||
            PROVIDERS[providerInfo.code]?.displayName ||
            providerInfo.code
          }
          isActive={isProviderActive(providerInfo)}
          onClick={() => setSelectedProvider(providerInfo.provider)}
          onFocus={() => setFocusedIndex(index)}
          tabIndex={
            isProviderActive(providerInfo) ? 0 : -1
          }
          role="tab"
          id={`provider-tab-${providerInfo.key}`}
          aria-controls={`provider-tabpanel-${providerInfo.key}`}
        />
      ))}
    </Section>
  );
}

ProviderTabList.propTypes = {
  providers: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
      mediaProviderId: PropTypes.string,
      displayName: PropTypes.string,
      provider: PropTypes.object.isRequired,
    })
  ).isRequired,
};

export default ProviderTabList;
